import { useEffect, useRef, useState } from "react";
import type { DifficultyFilter, LeaderboardView } from "@shared/types";
import {
  ALL_CATEGORIES,
  CATEGORY_LABELS,
  DIFFICULTY_FILTER_LABELS,
} from "@shared/config";
import { formatMs, formatPct } from "@shared/scoring";
import { getLeaderboard } from "../lib/api";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

type Mode = "solo" | "daily";
const DIFFICULTIES: DifficultyFilter[] = ["mixed", "easy", "medium", "hard"];
const MEDALS = ["🥇", "🥈", "🥉"];

export default function Leaderboard({
  initial,
  onHome,
}: {
  initial?: LeaderboardView | null;
  onHome: () => void;
}) {
  const [mode, setMode] = useState<Mode>(initial?.mode ?? "solo");
  const [category, setCategory] = useState<string>(initial?.category ?? "mixed");
  const [difficulty, setDifficulty] = useState<string>(
    initial?.difficulty ?? "mixed",
  );
  const [board, setBoard] = useState<LeaderboardView | null>(initial ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If we arrived with a board (e.g. right after posting a score), keep it —
  // it carries the "you" highlight — and only fetch once the player changes a
  // filter. Otherwise fetch immediately.
  const seeded = useRef(Boolean(initial));

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    getLeaderboard({ mode, category, difficulty })
      .then((r) => active && setBoard(r.board))
      .catch((e) => {
        if (active) {
          setBoard(null);
          setError(e instanceof Error ? e.message : "Couldn't load the leaderboard.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [mode, category, difficulty]);

  const entries = board?.entries ?? [];

  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={false} />
      <main className="mx-auto w-full max-w-lg px-4 pb-16 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            className="btn-chunk bg-white px-4 py-2 text-sm"
            onClick={onHome}
          >
            ← Home
          </button>
        </div>

        <div className="mb-4 flex justify-center">
          <Wordmark width={240} />
        </div>

        <div className="panel p-5 sm:p-7">
          <h2 className="text-center font-display text-2xl text-ink sm:text-3xl">
            🏆 Leaderboards
          </h2>

          {/* mode */}
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setMode("solo")}
              aria-pressed={mode === "solo"}
              className={`btn-chunk ${mode === "solo" ? "bg-flame text-white" : "bg-white"}`}
            >
              Solo · all-time
            </button>
            <button
              type="button"
              onClick={() => setMode("daily")}
              aria-pressed={mode === "daily"}
              className={`btn-chunk ${mode === "daily" ? "bg-grape text-white" : "bg-white"}`}
            >
              Daily · today
            </button>
          </div>

          {/* solo filters (the Daily board is always mixed/mixed) */}
          {mode === "solo" && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-ink/60">
                  Category
                </span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-2xl border-[3px] border-ink bg-white px-3 py-2.5 font-semibold text-ink outline-none focus:border-flame"
                >
                  <option value="mixed">All categories</option>
                  {ALL_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-ink/60">
                  Difficulty
                </span>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full rounded-2xl border-[3px] border-ink bg-white px-3 py-2.5 font-semibold text-ink outline-none focus:border-flame"
                >
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>
                      {DIFFICULTY_FILTER_LABELS[d]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {/* board */}
          <div className="mt-5">
            {loading ? (
              <p className="py-8 text-center font-semibold text-ink/40">
                Loading the standings…
              </p>
            ) : error ? (
              <p className="py-8 text-center font-semibold text-flame">{error}</p>
            ) : entries.length === 0 ? (
              <p className="py-8 text-center font-semibold text-ink/50">
                No scores here yet — be the first to post one! 🏁
              </p>
            ) : (
              <ol className="space-y-2">
                {entries.map((e) => (
                  <li
                    key={`${e.rank}-${e.displayName}-${e.totalMs}`}
                    className={`flex items-center gap-3 rounded-2xl border-[3px] px-3 py-2.5 ${
                      e.isYou
                        ? "border-flame bg-flame/10"
                        : "border-ink/15 bg-white"
                    }`}
                  >
                    <span className="w-8 shrink-0 text-center font-display text-lg text-ink">
                      {MEDALS[e.rank - 1] ?? e.rank}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-bold text-ink">
                      {e.displayName}
                      {e.isYou && (
                        <span className="ml-1.5 text-xs font-bold text-flame">you</span>
                      )}
                    </span>
                    <span className="shrink-0 text-right tabular-nums">
                      <span className="block font-display text-base text-ink">
                        {formatMs(e.totalMs)}
                      </span>
                      <span className="block text-xs font-semibold text-ink/50">
                        {e.correctCount}/{e.finishLine} · {formatPct(e.accuracy)}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-xs font-medium text-ink/50">
          Ranked by accuracy, then fastest answers. Finish a race and post your
          score to climb the board! 🏁
        </p>
        <p className="mt-2 text-center text-xs font-medium text-ink/50">
          Racer names are public here, so use a nickname — not your real name.{" "}
          <a href="/privacy" className="font-bold text-grape underline">
            Privacy
          </a>
        </p>
      </main>
    </div>
  );
}
